import { useCallback, useEffect, useRef } from "react";

interface UseHighlightScrollingOptions {
  /**
   * The container element to observe for changes
   */
  containerRef: React.RefObject<HTMLElement>;
  /**
   * Delay in milliseconds before scrolling to the highlight
   * @default 500
   */
  scrollDelay?: number;
  /**
   * CSS selector for the highlight elements to scroll to
   * @default ".source-highlight"
   */
  highlightSelector?: string;
}

/**
 * Hook that watches for changes to highlighted elements
 * and automatically scrolls to the first one after a delay.
 *
 * Uses MutationObserver to detect DOM changes instead of brittle timeouts.
 */
export function useHighlightScrolling({
  containerRef,
  scrollDelay = 200,
  highlightSelector = ".source-highlight",
}: UseHighlightScrollingOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const scrollToFirstHighlight = useCallback(() => {
    if (containerRef.current) {
      const highlights =
        containerRef.current.querySelectorAll(highlightSelector);
      if (highlights.length > 0) {
        highlights[0].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [containerRef, highlightSelector]);

  const scheduleScrolling = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule scrolling after the delay
    timeoutRef.current = setTimeout(scrollToFirstHighlight, scrollDelay);
  }, [scrollToFirstHighlight, scrollDelay]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new MutationObserver to watch for changes
    observerRef.current = new MutationObserver((mutations) => {
      let hasHighlightChanges = false;

      mutations.forEach((mutation) => {
        // Check for added nodes with highlight class
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              // Check if the added node matches the selector
              if (node.matches(highlightSelector)) {
                hasHighlightChanges = true;
              }
              // Check if any descendants match the selector
              else if (node.querySelectorAll(highlightSelector).length > 0) {
                hasHighlightChanges = true;
              }
            }
          });
        }
        // Check for attribute changes (class modifications)
        else if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class" &&
          mutation.target instanceof HTMLElement
        ) {
          if (mutation.target.matches(highlightSelector)) {
            hasHighlightChanges = true;
          }
        }
      });

      if (hasHighlightChanges) {
        scheduleScrolling();
      }
    });

    // Start observing
    observerRef.current.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [containerRef, scheduleScrolling, highlightSelector]);
}
